// ═══════════════════════════════════════════════════════════════
// 🎙️ THE LCA PODCAST — Notary listening page
// Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network
// Episodes live in Supabase (podcast_episodes + 'podcast' storage bucket).
// Published by the Podcast CMS — appears here instantly, no deploys.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

const POD_STYLES = `
  .pod-hero { background:#0D1B3E; border-radius:16px; padding:22px 24px; display:flex; align-items:center; gap:14px; margin-bottom:18px; }
  .pod-hero-icon { width:52px; height:52px; border-radius:12px; background:#C9A84C; display:flex; align-items:center; justify-content:center; font-size:26px; flex-shrink:0; }
  .pod-hero-title { font-size:19px; font-weight:800; color:#F8F7F4; }
  .pod-hero-sub { font-size:12.5px; color:#C9A84C; margin-top:2px; }
  .pod-card { background:#fff; border:1px solid #E8E6DF; border-radius:12px; padding:14px 16px; margin-bottom:12px; }
  .pod-card.playing { border-color:#C9A84C; box-shadow:inset 3px 0 0 #C9A84C; }
  .pod-row { display:flex; align-items:center; gap:12px; }
  .pod-play { width:42px; height:42px; border-radius:50%; background:#0D1B3E; border:none; color:#C9A84C; font-size:16px; cursor:pointer; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
  .pod-play:hover { background:#1A3A5E; }
  .pod-ep { font-size:11px; font-weight:800; color:#9A7A30; letter-spacing:0.04em; }
  .pod-new { font-size:10px; font-weight:800; background:#C9A84C; color:#0D1B3E; padding:2px 8px; border-radius:10px; margin-left:8px; }
  .pod-title { font-size:14.5px; font-weight:700; color:#0D1B3E; margin-top:2px; }
  .pod-desc { font-size:12px; color:#5F5E5A; margin-top:2px; line-height:1.5; }
  .pod-meta { display:flex; align-items:center; gap:14px; margin-top:6px; font-size:11.5px; color:#888780; flex-wrap:wrap; }
  .pod-meta a { color:#6B2FA0; text-decoration:none; font-weight:600; }
  .pod-meta a:hover { text-decoration:underline; }
  .pod-player { position:sticky; bottom:12px; background:#0D1B3E; border-radius:12px; padding:12px 18px; display:flex; align-items:center; gap:12px; margin-top:16px; box-shadow:0 6px 18px rgba(13,27,62,.25); }
  .pod-player-btn { background:none; border:none; color:#C9A84C; font-size:20px; cursor:pointer; flex-shrink:0; padding:0; }
  .pod-player-title { font-size:12px; font-weight:700; color:#F8F7F4; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .pod-bar { height:5px; background:#2A3B5E; border-radius:3px; margin-top:6px; cursor:pointer; position:relative; }
  .pod-bar-fill { height:5px; background:#C9A84C; border-radius:3px; pointer-events:none; }
  .pod-time { font-size:11px; color:#8FA3C0; flex-shrink:0; }
  .pod-speed { font-size:11px; font-weight:700; color:#C9A84C; border:1px solid #C9A84C; border-radius:10px; padding:2px 8px; background:none; cursor:pointer; flex-shrink:0; }
  .pod-empty { text-align:center; padding:40px 20px; color:#888780; font-size:13px; background:#fff; border:1px dashed #E0DCCF; border-radius:12px; }
`

function fmtTime(secs) {
  if (secs == null || isNaN(secs)) return '--:--'
  const m = Math.floor(secs / 60)
  const s = Math.floor(secs % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

function fmtDateTime(date) {
  if (!date) return ''
  const d = new Date(date)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' · ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

const NEW_WINDOW_DAYS = 10
function isNewEpisode(publishedAt) {
  if (!publishedAt) return false
  return (Date.now() - new Date(publishedAt).getTime()) < NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000
}

const SPEEDS = [1, 1.25, 1.5]

export default function TribePodcast() {
  const [episodes, setEpisodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [current, setCurrent] = useState(null)   // episode object
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)    // seconds
  const [duration, setDuration] = useState(0)
  const [speedIdx, setSpeedIdx] = useState(0)
  const audioRef = useRef(null)
  const barRef = useRef(null)

  useEffect(() => {
    async function load() {
      try {
        const { data } = await supabase
          .from('podcast_episodes')
          .select('*')
          .eq('is_published', true)
          .order('episode_number', { ascending: false })
        setEpisodes(data || [])
      } catch (e) { console.warn('[Podcast] load error:', e.message) }
      setLoading(false)
    }
    load()
  }, [])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const onTime = () => setProgress(audio.currentTime)
    const onMeta = () => setDuration(audio.duration || 0)
    const onEnd = () => setPlaying(false)
    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('loadedmetadata', onMeta)
    audio.addEventListener('ended', onEnd)
    return () => {
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('loadedmetadata', onMeta)
      audio.removeEventListener('ended', onEnd)
    }
  }, [current])

  function togglePlay(ep) {
    const audio = audioRef.current
    if (!audio) return
    if (current?.id === ep.id) {
      if (playing) { audio.pause(); setPlaying(false) }
      else { audio.play(); setPlaying(true) }
      return
    }
    setCurrent(ep)
    setProgress(0)
    setDuration(ep.duration_seconds || 0)
    setTimeout(() => {
      const a = audioRef.current
      if (a) {
        a.src = ep.audio_url
        a.playbackRate = SPEEDS[speedIdx]
        a.play().then(() => setPlaying(true)).catch(() => setPlaying(false))
      }
    }, 0)
  }

  function seek(e) {
    const audio = audioRef.current
    const bar = barRef.current
    if (!audio || !bar || !duration) return
    const rect = bar.getBoundingClientRect()
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    audio.currentTime = frac * duration
    setProgress(frac * duration)
  }

  function cycleSpeed() {
    const next = (speedIdx + 1) % SPEEDS.length
    setSpeedIdx(next)
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next]
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <style>{POD_STYLES}</style>

      <div className="pod-hero">
        <div className="pod-hero-icon">🎙️</div>
        <div>
          <div className="pod-hero-title">The LCA Podcast</div>
          <div className="pod-hero-sub">Learn on the go — between signings, on the road, anywhere 💜</div>
        </div>
      </div>

      {loading ? (
        <div className="pod-empty">Loading episodes...</div>
      ) : episodes.length === 0 ? (
        <div className="pod-empty">🎙️ The first episode is being recorded — stay tuned, tribe!!</div>
      ) : episodes.map(ep => (
        <div key={ep.id} className={`pod-card${current?.id === ep.id ? ' playing' : ''}`}>
          <div className="pod-row">
            <button className="pod-play" onClick={() => togglePlay(ep)}
              aria-label={current?.id === ep.id && playing ? `Pause episode ${ep.episode_number}` : `Play episode ${ep.episode_number}`}>
              {current?.id === ep.id && playing ? '❚❚' : '▶'}
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span className="pod-ep">EPISODE {ep.episode_number}</span>
              {isNewEpisode(ep.published_at) && <span className="pod-new">NEW</span>}
              <div className="pod-title">{ep.title}</div>
              {ep.description && <div className="pod-desc">{ep.description}</div>}
              <div className="pod-meta">
                <span>⏱ {fmtTime(ep.duration_seconds)}</span>
                <span>{fmtDateTime(ep.published_at)}</span>
                <a href={ep.audio_url} download target="_blank" rel="noreferrer">⬇ Save</a>
              </div>
            </div>
          </div>
        </div>
      ))}

      {current && (
        <div className="pod-player">
          <button className="pod-player-btn" onClick={() => togglePlay(current)}
            aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? '❚❚' : '▶'}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pod-player-title">{current.title}</div>
            <div className="pod-bar" ref={barRef} onClick={seek}>
              <div className="pod-bar-fill" style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
            </div>
          </div>
          <span className="pod-time">{fmtTime(progress)} / {fmtTime(duration)}</span>
          <button className="pod-speed" onClick={cycleSpeed}>{SPEEDS[speedIdx]}x</button>
        </div>
      )}

      <audio ref={audioRef} preload="metadata" />
    </div>
  )
}
